import {
  ActionReducerMapBuilder,
  Dispatch,
  Draft,
  PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import {
  BebyggelseobjektPost,
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastPutRequest,
  HTTP_STATUS,
  ModelError,
  SammansattReferensParam,
  State
} from 'models';

import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from './storeHelpers';

import BebyggelseApi from 'api/bebyggelseApi';
import { reservationSlice } from 'api/endpoints/reservationSlice';

export const fetchUtkast = createAsyncThunk<BebyggelseobjektUtkast, string, { rejectValue: ModelError }>(
  'get/utkast',
  async (id: string, { rejectWithValue }): Promise<any> => {
    const result = await BebyggelseApi.utkast.get(id);
    return errorOr(result, rejectWithValue);
  }
);

export const deleteUtkast = createAsyncThunk<undefined, string, { rejectValue: ModelError }>(
  'delete/utkast',
  async (id: string, { rejectWithValue }): Promise<any> => {
    const response = await BebyggelseApi.utkast.remove(id);
    return errorOr<undefined>(response, rejectWithValue);
  }
);

export const postUtkast = createAsyncThunk<BebyggelseobjektUtkast, BebyggelseobjektPost, { rejectValue: ModelError }>(
  'post/utkast',
  async (utkast: BebyggelseobjektPost, { rejectWithValue }): Promise<any> => {
    const response = await BebyggelseApi.utkast.post(utkast);
    return errorOr(response, rejectWithValue);
  }
);

export const patchPublicering = createAsyncThunk<
  BebyggelseobjektUtkast | undefined,
  string,
  { rejectValue: ModelError }
>('patch/utkast/publicera', async (id: string, { rejectWithValue }): Promise<any> => {
  const response = await BebyggelseApi.utkast.publicera(id);
  return errorOr<BebyggelseobjektUtkast | undefined>(
    response,
    rejectWithValue,
    [],
    false,
    response.status === HTTP_STATUS.CREATED
  );
});

export const putUtkast = createAsyncThunk<
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastPutRequest,
  { rejectValue: ModelError }
>('put/utkast', async (utkast: BebyggelseobjektUtkastPutRequest, { rejectWithValue }): Promise<any> => {
  const response = await BebyggelseApi.utkast.put(utkast);
  return errorOr(response, rejectWithValue, [HTTP_STATUS.BAD_REQUEST]);
});

export const patchUtkastSammansattReferens = createAsyncThunk<
  null,
  { utkast_id: string; body: SammansattReferensParam },
  { rejectValue: ModelError }
>(
  'patch/bebyggelseobjektutkast/referens',
  async (request: { utkast_id: string; body: SammansattReferensParam }, { rejectWithValue }): Promise<any> => {
    const response = await BebyggelseApi.utkast.patchSammansattReferens(request.utkast_id, request.body);
    return errorOr(response, rejectWithValue);
  }
);

export const deleteUtkastSammansattReferens = createAsyncThunk<
  null,
  { utkast_id: string; bebyggelseobjekt_id: string },
  { rejectValue: ModelError }
>(
  'delete/bebyggelseobjektutkast/referens',
  async (request: { utkast_id: string; bebyggelseobjekt_id: string }, { rejectWithValue }): Promise<any> => {
    const response = await BebyggelseApi.utkast.deleteSammansattReferens(
      request.utkast_id,
      request.bebyggelseobjekt_id
    );
    return errorOr(response, rejectWithValue);
  }
);

const initialState: State<BebyggelseobjektUtkast> = {
  requestStatus: 'idle',
  requestType: undefined,
  data: undefined,
  currentRequestId: undefined,
  error: undefined
};

export const clearUtkast = () => {
  return (dispatch: Dispatch) => {
    dispatch(clearUtkastState());
  };
};

const utkastSlice = createSlice({
  name: 'utkast',
  initialState,
  reducers: {
    clearUtkastState: (state: Draft<State<BebyggelseobjektUtkast>>): void => {
      state.data = undefined;
    },
    updateVinjett: (state: Draft<State<BebyggelseobjektUtkast>>, payloadAction: PayloadAction<string>): void => {
      if (state.data?.vinjett) {
        state.data.vinjett.url = payloadAction.payload;
      }
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<State<BebyggelseobjektUtkast>>>): void => {
    //FETCH
    builder.addCase(
      fetchUtkast.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      fetchUtkast.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: FulfilledActionType<BebyggelseobjektUtkast>): void =>
        fulfilled(state, action)
    );
    builder.addCase(
      fetchUtkast.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );

    //POST
    builder.addCase(
      postUtkast.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      postUtkast.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: FulfilledActionType<BebyggelseobjektUtkast>): void => {
        fulfilled(state, action);
      }
    );
    builder.addCase(postUtkast.rejected, (state: Draft<State<BebyggelseobjektUtkast>>, action): void =>
      rejected(state, action)
    );

    //DELETE
    builder.addCase(
      deleteUtkast.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(deleteUtkast.fulfilled, (state: Draft<State<BebyggelseobjektUtkast>>, action): void =>
      fulfilled(state, action)
    );
    builder.addCase(
      deleteUtkast.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );

    // PATCH REFERENS
    builder.addCase(
      patchUtkastSammansattReferens.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      patchUtkastSammansattReferens.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: FulfilledActionType<null>): void => fulfilled(state, action)
    );
    builder.addCase(
      patchUtkastSammansattReferens.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );

    // DELETE REFERENS
    builder.addCase(
      deleteUtkastSammansattReferens.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      deleteUtkastSammansattReferens.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: FulfilledActionType<null>): void => fulfilled(state, action)
    );
    builder.addCase(
      deleteUtkastSammansattReferens.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );

    // PATCH PUBLICERING
    builder.addCase(
      patchPublicering.pending,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      patchPublicering.fulfilled,
      (
        state: Draft<State<BebyggelseobjektUtkast>>,
        action: FulfilledActionType<BebyggelseobjektUtkast | undefined>
      ): void => {
        // Om publicera utan granskning
        if (action.payload == undefined) {
          const data = { ...state.data } as BebyggelseobjektUtkast;
          const newAction: FulfilledActionType<BebyggelseobjektUtkast> = {
            payload: data,
            meta: action.meta,
            type: action.type
          };
          fulfilled(state, newAction);
        } else {
          fulfilled(state, action);
        }
      }
    );
    builder.addCase(
      patchPublicering.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );
    //PUT
    builder.addCase(putUtkast.pending, (state: Draft<State<BebyggelseobjektUtkast>>, action: PendingActionType): void =>
      pending(state, action)
    );
    builder.addCase(
      putUtkast.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: FulfilledActionType<BebyggelseobjektUtkast>): void =>
        fulfilled(state, action)
    );
    builder.addCase(
      putUtkast.rejected,
      (state: Draft<State<BebyggelseobjektUtkast>>, action: RejectedActionType): void => rejected(state, action)
    );

    // Uppdatera state för utkast ifall reservation sker och är lyckad
    builder.addMatcher(reservationSlice.endpoints.reserveBebyggelseutkast.matchFulfilled, (state, { payload }) => {
      state.data = payload;
    });
  }
});

export const { clearUtkastState, updateVinjett } = utkastSlice.actions;

export default utkastSlice.reducer;
